<template>
  <v-card>
    <v-card-title>
      {{$t('product_create')}}
    </v-card-title>

    <v-stepper v-model="step" flat class="creation-stepper">
      <v-stepper-header>
        <v-stepper-step
          step="1"
          :complete="step > 1"
          :color="step > 1 ? 'grey' : 'primary'">
          {{ step > 1 ? $t('product_created') : $t('product_create') }}
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step
          step="2"
          :complete="step > 2"
          color="primary">
          {{ $t('set_up_page_after_scan') }}
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Step 1: product info -->
        <v-stepper-content step="1">
          <div class="step-body d-flex flex-column flex-md-row">
            <div class="image-ctn mr-md-6 mb-4 mb-md-0">
              <img class="product-image" :src="productImage">
              <image-cropper
                input-key="product-creation-image"
                :gallery-only="true"
                :show-select-dialog="true"
                @image-selected="onImageSelect">
              </image-cropper>
            </div>

            <div class="flex-grow-1">
              <v-form v-model="valid" ref="product-form">
                <v-text-field
                  outlined
                  dense
                  persistent-hint
                  validate-on-blur
                  class="field-row"
                  v-model="productForm.name"
                  name="name"
                  :label="`${$t('product_name')}*`"
                  :hint="$t('product_name_hint')"
                  :rules="rules.name"
                  :error-messages="errorMessages.name"
                  @focus="clearErrors('name')"
                  required></v-text-field>
                <v-text-field
                  outlined
                  dense
                  persistent-hint
                  validate-on-blur
                  class="field-row"
                  v-model="productForm.gtin"
                  name="gtin"
                  :counter="14"
                  :label="$t('gtin')"
                  :hint="$t('product_gtin_hint')"
                  :rules="gtinRules"
                  :error-messages="errorMessages.gtin"
                  @focus="clearErrors('gtin')"></v-text-field>
                <v-text-field
                  outlined
                  dense
                  persistent-hint
                  v-model="productForm.sku"
                  name="sku"
                  :label="$t('product_internal_name')"
                  :hint="$t('product_sku_hint')"
                  :rules="rules.sku"
                  :error-messages="errorMessages.sku"
                  @focus="clearErrors('sku')"></v-text-field>
              </v-form>

              <div class="inline-hint mt-4">
                <v-icon size="20" color="primary" class="mr-2">mdi-lightbulb-outline</v-icon>
                <span>{{ $t('product_details_update_later_hint') }}</span>
              </div>
            </div>
          </div>

          <v-card-actions class="step-actions">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="handleClose()" :disabled="savingProduct">{{$t('close')}}</v-btn>
            <v-btn
              raised
              color="primary"
              @click="handleNext()"
              :loading="savingProduct"
              :disabled="!productForm.name || savingProduct">{{$t('next')}}</v-btn>
          </v-card-actions>
        </v-stepper-content>

        <!-- Step 2: destination -->
        <v-stepper-content step="2">
          <div class="step-body">
            <div class="step-title">{{ $t('scan_destination_title') }}</div>
            <div class="step-hint">{{ $t('scan_destination_hint') }}</div>

            <destination-cards
              class="mt-6"
              orientation="vertical"
              :value="destinationType"
              @input="onDestinationSelect">
            </destination-cards>

            <div v-if="destinationType === 'existing'">
              <v-divider class="my-5"></v-divider>
              <v-autocomplete
                ref="existingSelect"
                v-model="form.stc_config"
                :items="existingLandingPages"
                :loading="landingPagesLoading"
                :search-input.sync="landingPageSearch"
                :no-filter="true"
                :label="$t('select_landing_page')"
                :no-data-text="$t('no_data_available')"
                :menu-props="{ offsetY: true }"
                :rules="rules.stc_config"
                item-value="id"
                item-text="name"
                outlined
                dense
                hide-details="auto"
                clearable>
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center" style="width: 100%">
                    <span class="flex-grow-1 text-truncate">{{ item.name }}</span>
                    <span class="page-tag">{{ landingPageLayoutTag(item) }}</span>
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <span class="text-truncate">{{ item.name }} - {{ landingPageLayoutTag(item) }}</span>
                </template>
              </v-autocomplete>
            </div>

            <div v-if="destinationType === 'redirect'">
              <v-divider class="my-5"></v-divider>
              <v-form ref="urlForm" @submit.prevent>
                <v-text-field
                  v-model="form.url"
                  :label="$t('url')"
                  :rules="urlRules"
                  placeholder="https://www.example.com"
                  validate-on-blur
                  outlined
                  dense
                  hide-details="auto"></v-text-field>
              </v-form>
            </div>
          </div>

          <v-card-actions class="step-actions">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="handleClose()" :disabled="savingDestination">{{$t('close')}}</v-btn>
            <v-btn
              raised
              color="primary"
              @click="handleSaveDestination()"
              :loading="savingDestination">{{$t('save')}}</v-btn>
          </v-card-actions>
        </v-stepper-content>

        <!-- Step 3: confirmation -->
        <v-stepper-content step="3">
          <div class="step-body success-body">
            <v-icon size="60" color="#3fc135">mdi-check-circle-outline</v-icon>
            <div class="success-title mt-4">{{ successTitle }}</div>
            <div class="success-text mt-1">{{ successText }}</div>
            <div class="inline-hint justify-center mt-6">
              <v-icon size="20" color="primary" class="mr-2">mdi-lightbulb-outline</v-icon>
              <span>{{ $t('change_destination_anytime_hint') }}</span>
            </div>
          </div>

          <v-card-actions class="step-actions">
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="handleClose()">{{$t('close')}}</v-btn>
            <v-btn
              v-if="destinationType === 'dpp'"
              raised
              color="primary"
              @click="openDppEditor()">{{$t('add_product_data')}}</v-btn>
            <v-btn
              v-else-if="destinationType === 'custom'"
              raised
              color="primary"
              @click="openLandingPageEditor()">{{$t('open_editor')}}</v-btn>
          </v-card-actions>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-card>
</template>

<script>

import Utils from '@/utils/utils'
import { mapGetters, mapMutations } from 'vuex'
import amplitude from 'amplitude-js'
import DefaultLandingPageTemplate from '@/const/templates/default_landing_page'
import DestinationCards from '@/components/redirection/DestinationCards.vue'
import ImageCropper from '@/components/utils/ImageCropper'
import { buildDestinationPayload, urlRules } from '@/utils/destination'

export default {
  name: 'ProductForm',
  components: { DestinationCards, ImageCropper },
  props: ['campaign'],
  data () {
    return {
      step: 1,
      valid: false,
      savingProduct: false,
      savingDestination: false,
      productCreated: false,
      errorMessages: {
        name: [],
        sku: [],
        gtin: []
      },
      product: {},
      productForm: {
        name: '',
        sku: '',
        client_url: '',
        description: '',
        gtin: '',
        image: null,
        gallery_image: null,
        campaign: null
      },
      destinationType: 'dpp',
      destinationId: null,
      customPageId: null,
      form: { url: '', stc_config: null },
      existingLandingPages: [],
      landingPagesLoading: false,
      landingPageSearch: null,
      debounceSearch: null,
      rules: {
        name: [
          v => !!v || this.$t('field_required'),
          v => (v && v.length <= 100) || this.$t('field_characters_limit', { count: 100 })
        ],
        sku: [
          v => (v || '').length <= 50 || this.$t('field_characters_limit', { count: 50 })
        ],
        stc_config: [
          v => !!v || this.$t('field_required')
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['defaultBrand', 'landingPages', 'defaultRedirectionRule', 'profile']),

    gtinRules () {
      return [
        v => (!v || /^[0-9]+$/.test(v)) || this.$t('gs1_field_numeric_allowed'),
        v => (!v || v.length === 14) || this.$t('gs1_gtin_fourteen_characters')
      ]
    },

    urlRules () {
      return urlRules(this.$t.bind(this))
    },

    productImage () {
      return this.productForm.image ? this.productForm.image : require('@/assets/product-placeholder.png')
    },

    selectedExistingPageName () {
      const page = this.existingLandingPages.find(item => item.id === this.form.stc_config)
      return page ? page.name : ''
    },

    successTitle () {
      return ['dpp', 'custom'].includes(this.destinationType)
        ? this.$t('product_ready_title')
        : this.$t('product_and_page_ready_title')
    },

    successText () {
      switch (this.destinationType) {
        case 'dpp':
          return this.$t('dpp_still_empty_hint')
        case 'custom':
          return this.$t('landing_page_still_empty_hint')
        case 'existing':
          return this.$t('scans_will_show_page_hint', { name: this.selectedExistingPageName })
        default:
          return this.$t('scans_will_redirect_to_hint', { url: this.form.url })
      }
    }
  },
  methods: {
    ...mapMutations({
      setRenameCodeError: 'SET_RENAME_CODE_ERROR',
      setRenameCodeErrorMessage: 'SET_RENAME_CODE_ERROR_MESSAGE'
    }),

    clearErrors (key) {
      if (key) {
        this.errorMessages[key] = []
      } else {
        this.errorMessages = {
          name: [],
          sku: [],
          gtin: []
        }
      }
    },

    handleSubmitError (errors) {
      errors.forEach((error) => {
        if (error.key === 'scm.code_rename.exists') {
          this.errorMessages.gtin.push(this.$t('scm.code_rename.exists'))
        } else {
          this.errorMessages.gtin.push(error.message)
        }
      })
      this.savingProduct = false
    },

    resetForm () {
      if (this.$refs['product-form']) {
        this.$refs['product-form'].reset()
      }
      this.step = 1
      this.productForm = {
        name: '',
        sku: '',
        client_url: '',
        description: '',
        gtin: '',
        image: null,
        gallery_image: null,
        campaign: this.campaign
      }
      this.product = {}
      this.savingProduct = false
      this.savingDestination = false
      this.productCreated = false
      this.destinationType = 'dpp'
      this.destinationId = null
      this.customPageId = null
      this.form = { url: '', stc_config: null }
      this.setRenameCodeError(false)
      this.setRenameCodeErrorMessage({ errorMessage: [] })
    },

    handleClose () {
      this.resetForm()
      this.$emit('on-close-dialog')
    },

    onImageSelect (image, fromGallery = false) {
      this.productForm = fromGallery
        ? Object.assign({}, this.productForm, { image: image.file, gallery_image: image.key })
        : Object.assign({}, this.productForm, { image: image, gallery_image: null })
    },

    async handleNext () {
      this.$refs['product-form'].validate()
      if (!this.valid) {
        return
      }
      this.savingProduct = true

      if (this.productCreated) {
        this.$emit('on-product-created', { product: this.product, gtin: this.productForm.gtin })
        return
      }

      if (!this.productForm.campaign) {
        this.productForm.campaign = this.campaign
      }

      if (!this.productForm.sku) {
        this.productForm.sku = Utils.slugify(this.productForm.name, 50)
      }
      try {
        if (this.defaultBrand && !this.productForm.brand) {
          this.productForm.brand = this.defaultBrand.reference
        }
        this.product = await this.$store.dispatch('createProduct', { product: this.productForm })
        this.productCreated = true
        amplitude.getInstance().logEvent('Product Added')
        this.$emit('on-product-created', { product: this.product, gtin: this.productForm.gtin })
      } catch (err) {
        for (const key in err.data) {
          this.errorMessages[key] = err.data[key]
        }
        this.savingProduct = false
        this.productCreated = false
      }
    },

    goToDestinationStep ({ product, destinationId }) {
      this.product = product
      this.destinationId = destinationId
      this.customPageId = product?.stc_data?.scantrust_landing_page || null
      this.savingProduct = false
      this.step = 2
    },

    stopSaving () {
      this.savingProduct = false
    },

    layoutFromConfig (configJson) {
      return configJson?.editor?.type === 'dpp' ? 'dpp' : 'showcase'
    },

    landingPageLayoutTag (item) {
      return this.layoutFromConfig(item?.config_json) === 'dpp' ? this.$t('dpp') : this.$t('showcase')
    },

    onDestinationSelect (type) {
      this.destinationType = type
      if (type === 'redirect' && !this.form.url) {
        this.form.url = this.profile?.company?.website || ''
      }
      if (type === 'existing' && !this.existingLandingPages.length) {
        this.loadPages()
      }
    },

    async loadPages () {
      this.landingPagesLoading = true
      await this.$store.dispatch('loadLandingPages')
      this.existingLandingPages = this.landingPages.slice()
      this.landingPagesLoading = false
    },

    async runLandingPageSearch () {
      this.landingPagesLoading = true
      const searchText = this.landingPageSearch || ''
      const res = await this.$store.dispatch('searchLandingPages', { search: searchText }).catch(() => null)
      if (searchText !== (this.landingPageSearch || '')) return
      if (res) this.existingLandingPages = res
      this.landingPagesLoading = false
    },

    async createCustomPage () {
      const id = await this.$store.dispatch('createLandingPage', { name: this.product.name, configJson: DefaultLandingPageTemplate })
      await this.$store.dispatch('updateProductJson', { id: this.product.id, json: { scantrust_landing_page: id } })
      this.customPageId = id
    },

    async handleSaveDestination () {
      if (this.destinationType === 'redirect' && !this.$refs.urlForm.validate()) return
      if (this.destinationType === 'existing' && !this.$refs.existingSelect.validate(true)) return

      this.savingDestination = true
      try {
        if (this.destinationType === 'custom' && !this.customPageId) {
          await this.createCustomPage()
        }
        const payload = buildDestinationPayload({
          destinationType: this.destinationType,
          url: this.form.url,
          stcConfig: this.form.stc_config,
          customPageId: this.customPageId
        })
        if (this.destinationId) {
          await this.$store.dispatch('updateRuleDestination', {
            ruleId: this.defaultRedirectionRule.id,
            destination: { ...payload, id: this.destinationId }
          })
        }
        this.step = 3
      } finally {
        this.savingDestination = false
      }
    },

    openDppEditor () {
      const id = this.product.id
      this.resetForm()
      this.$router.push({ name: 'products.dpp', params: { id } })
    },

    openLandingPageEditor () {
      const id = this.customPageId
      const productId = this.product.id
      if (!id) return
      this.resetForm()
      this.$router.push({ name: 'landing-page.edit', params: { id }, query: { product: productId, from: 'product-details' } })
    }
  },
  watch: {
    landingPageSearch () {
      if (this.debounceSearch) this.debounceSearch()
    }
  },
  created () {
    this.productForm.campaign = this.campaign
    this.debounceSearch = Utils.debounce(() => this.runLandingPageSearch(), 400)
  }
}

</script>

<style lang="sass" scoped>

.creation-stepper
  box-shadow: none

.creation-stepper ::v-deep .v-stepper__header
  box-shadow: none
  border-bottom: 1px solid #ededed

.creation-stepper ::v-deep .v-stepper__content
  padding: 0

.step-body
  padding: 24px
  min-height: 300px

.step-actions
  padding: 20px 24px
  border-top: 1px solid #ededed

.image-ctn
  width: 220px
  flex-shrink: 0

.product-image
  width: 200px
  height: 200px
  object-fit: cover
  display: block
  margin: 0 auto 20px

.field-row
  margin-bottom: 16px

.step-title
  font-family: 'Inter', sans-serif
  font-size: 20px
  font-weight: 500
  color: #242424

.step-hint
  font-size: 14px
  color: #767676
  margin-top: 4px

.inline-hint
  display: flex
  align-items: center
  font-size: 16px
  color: #767676

.page-tag
  font-size: 12px
  color: #999
  margin-left: 12px
  flex-shrink: 0

.success-body
  display: flex
  flex-direction: column
  align-items: center
  text-align: center
  padding-top: 40px

.success-title
  font-family: 'Inter', sans-serif
  font-size: 22px
  font-weight: 700
  color: #242424

.success-text
  font-size: 20px
  color: #242424
  max-width: 830px

</style>
